import type { BaseDocument } from '../mongo/base-document.interface';
import type { VehicleProfile } from '../vehicle-template';

export const VEHICLE_STATUS = {
  AVAILABLE: 'available',
  IN_MAINTENANCE: 'in-maintenance',
} as const;

export type VehicleStatus = (typeof VEHICLE_STATUS)[keyof typeof VEHICLE_STATUS];

export interface Vehicle extends BaseDocument {
  carrierId: string;
  defaultDriverId?: string;
  licensePlate: string;
  siteIds: string[];
  spaceId: string;
  status: VehicleStatus;
  vehicleTemplateId: string;
}

/**
 * A resolved name reference: the original ID plus the entity's display name.
 */
export interface ResolvedRef {
  id: string;
  name: string;
}

/**
 * A resolved vehicle template reference: extends ResolvedRef with an optional profile field
 * so the frontend can render profile-specific icons.
 */
export interface ResolvedVehicleTemplateRef extends ResolvedRef {
  profile?: VehicleProfile;
}

/**
 * Vehicle enriched with resolved reference names for display purposes.
 * Computed at response time — never stored in the database.
 */
export interface VehicleWithResolvedRefs extends Vehicle {
  resolvedCarrier: ResolvedRef;
  resolvedDefaultDriver?: ResolvedRef;
  resolvedSites: ResolvedRef[];
  resolvedVehicleTemplate: ResolvedVehicleTemplateRef;
}

export type CreateVehicle = Omit<Vehicle, keyof BaseDocument>;
// spaceId is non-transferable (a vehicle stays in its space); carrierId can be reassigned in space context.
export type UpdateVehicleBase = Partial<Omit<CreateVehicle, 'spaceId'>>;

/**
 * Body sent to PATCH /vehicles/:id.
 * `defaultDriverId` can be set to `null` to explicitly clear an assigned driver.
 */
export type UpdateVehicle = Omit<UpdateVehicleBase, 'defaultDriverId'> & { defaultDriverId?: string | null };
